
import { initializeApp } from 'firebase/app';
import { 
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  updateDoc, 
  doc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  onSnapshot,
  arrayUnion,
  increment
} from 'firebase/firestore';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  browserLocalPersistence,
  setPersistence
} from 'firebase/auth';
import { ClientData, PhotoSession, SessionStatus, StudioSettings, ChatMessage, DriveHistoryEntry } from '../types/index';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Validate required environment variables
const requiredEnvVars = ['VITE_FIREBASE_API_KEY', 'VITE_FIREBASE_AUTH_DOMAIN', 'VITE_FIREBASE_PROJECT_ID', 'VITE_FIREBASE_APP_ID'];
const missingEnvVars = requiredEnvVars.filter(envVar => !import.meta.env[envVar]);

if (missingEnvVars.length > 0) {
  throw new Error(`Missing required environment variables: ${missingEnvVars.join(', ')}. Please check your .env.local file.`);
}

const app = initializeApp(firebaseConfig);
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  }),
  experimentalForceLongPolling: true,
});
const auth = getAuth(app);

// Enable persistence
setPersistence(auth, browserLocalPersistence).catch(console.error);

export const firebaseService = {
  getAuth: () => auth,
  loginAdmin: async (email: string, pass: string) => {
    return await signInWithEmailAndPassword(auth, email, pass);
  },

  getSettings: (): StudioSettings => ({ 
    google_api_key: import.meta.env.VITE_GOOGLE_API_KEY || (() => {
      throw new Error('VITE_GOOGLE_API_KEY is required. Please check your .env.local file.');
    })()
  }),

  getClients: async (): Promise<ClientData[]> => {
    const q = query(collection(db, "clients"), orderBy("client_name", "asc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as ClientData));
  },

  addClient: async (client: Omit<ClientData, 'id'>): Promise<ClientData> => {
    const docRef = await addDoc(collection(db, "clients"), client);
    return { id: docRef.id, ...client };
  },

  getSessions: async (): Promise<PhotoSession[]> => {
    const q = query(collection(db, "sessions"), orderBy("created_at", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as PhotoSession));
  },

  subscribeToSessions: (callback: (sessions: PhotoSession[]) => void) => {
    const q = query(collection(db, "sessions"), orderBy("created_at", "desc"));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as PhotoSession));
      callback(data);
    });
  },

  subscribeToSession: (sessionId: string, callback: (session: PhotoSession) => void) => {
    return onSnapshot(doc(db, "sessions", sessionId), (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() } as PhotoSession);
      }
    });
  },

  createSession: async (session: Omit<PhotoSession, 'id' | 'status' | 'created_at' | 'selected_files' | 'expiry_date'>, expiryDays: number = 30): Promise<string> => {
    const now = Date.now();
    const expiryTime = expiryDays * 24 * 60 * 60 * 1000;
    
    const newSession = {
      ...session,
      status: SessionStatus.OPEN,
      created_at: now,
      expiry_date: now + expiryTime,
      selected_files: [],
      comments: {},
      messages: [],
      drive_history: [{
        type: 'original',
        url: `https://drive.google.com/drive/folders/${session.drive_folder_id}`,
        timestamp: now,
        folderId: session.drive_folder_id
      }],
      is_confirmed: false
    };
    const docRef = await addDoc(collection(db, "sessions"), newSession);
    return docRef.id;
  },

  deleteSession: async (sessionId: string) => {
    await deleteDoc(doc(db, "sessions", sessionId));
  },

  extendExpiry: async (sessionId: string, days: number) => {
    const docRef = doc(db, "sessions", sessionId);
    const extraTime = days * 24 * 60 * 60 * 1000;
    await updateDoc(docRef, {
      expiry_date: increment(extraTime)
    });
  },

  validateAccessCode: async (code: string): Promise<PhotoSession | null> => {
    const q = query(collection(db, "sessions"), where("access_code", "==", code.trim().toUpperCase()));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const session = { id: snap.docs[0].id, ...snap.docs[0].data() } as PhotoSession;
    
    // Validasi masa berlaku
    if (Date.now() > session.expiry_date) {
      throw new Error("EXPIRED");
    }
    
    return session;
  },

  getSessionById: async (sessionId: string): Promise<PhotoSession | null> => {
    const docRef = doc(db, "sessions", sessionId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    
    const session = { id: snap.id, ...snap.data() } as PhotoSession;
    
    // Validasi masa berlaku
    if (Date.now() > session.expiry_date) {
      throw new Error("EXPIRED");
    }
    
    return session;
  },

  updateSessionSelection: async (sessionId: string, selections: string[]) => {
    const docRef = doc(db, "sessions", sessionId);
    await updateDoc(docRef, {
      selected_files: selections,
      status: SessionStatus.SUBMITTED
    });
  },

  sendMessage: async (sessionId: string, message: Omit<ChatMessage, 'id'>) => {
    const docRef = doc(db, "sessions", sessionId);
    const msgWithId = { ...message, id: Math.random().toString(36).substr(2, 9), read: false };
    await updateDoc(docRef, {
      messages: arrayUnion(msgWithId)
    });
  },

  markMessagesAsRead: async (sessionId: string, viewerRole: 'admin' | 'client', currentMessages: ChatMessage[]) => {
    const docRef = doc(db, "sessions", sessionId);
    const updatedMessages = currentMessages.map(msg => {
      // If message was sent by the OTHER role and is currently unread
      if (msg.sender !== viewerRole && !msg.read) {
        return { ...msg, read: true };
      }
      return msg;
    });
    
    // Check if any changes actually occurred
    const hasChanges = updatedMessages.some((msg, idx) => msg.read !== currentMessages[idx].read);
    
    if (hasChanges) {
      await updateDoc(docRef, { messages: updatedMessages });
    }
  },

  updatePhotoComment: async (sessionId: string, filename: string, comment: string) => {
    const docRef = doc(db, "sessions", sessionId);
    await updateDoc(docRef, {
      [`comments.${filename.replace(/\./g, '_')}`]: comment
    });
  },

  updateSessionStatus: async (sessionId: string, status: SessionStatus) => {
    const docRef = doc(db, "sessions", sessionId);
    await updateDoc(docRef, { status });
  },

  addEditedFolder: async (sessionId: string, folderId: string, files: any[], url: string) => {
    const docRef = doc(db, "sessions", sessionId);
    const historyEntry: DriveHistoryEntry = {
      type: 'edited',
      url: url,
      timestamp: Date.now(),
      folderId: folderId
    };
    
    await updateDoc(docRef, {
      edited_folder_id: folderId,
      edited_files: files,
      status: SessionStatus.EDITED,
      drive_history: arrayUnion(historyEntry)
    });
  }
};
